import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, untracked } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { I18nService } from '#core/i18n/i18n.service';
import { RealtimeMessagingService } from '#core/services/realtime-messaging/realtime-messaging.service';
import { SessionIdentitySyncService } from '#core/services/session-identity-sync.service';
import { SessionStore } from '#core/store/session.store';
import { GalleryImportMediaV2Component } from '#modules/gallery/views/gallery/components/gallery-import-media/gallery-import-media-v2.component';
import { RouterListenerService } from '#shared/services/router-listener.service';

@Component({
    templateUrl: './logged-in-layout.component.html',
    styleUrls: [],
    imports: [RouterOutlet, GalleryImportMediaV2Component],
    // Keep default change detection strategy for the layout because it contains router-outlet and so depend on many others views
    changeDetection: ChangeDetectionStrategy.Default,
})
export class LoggedInLayoutComponent {
    protected readonly sessionStore = inject(SessionStore);

    private readonly _realtimeMessagingService = inject(RealtimeMessagingService);
    private readonly _routerListenerService = inject(RouterListenerService);
    private readonly _i18nService = inject(I18nService);
    private readonly _sessionIdentitySyncService = inject(SessionIdentitySyncService);
    private readonly _destroyRef = inject(DestroyRef);

    constructor() {
        this._routerListenerService.init();
        // Logged-out pages do not listen: they show no identity, and some replay side effects on load (the invitation page logs out, the scan page records a scan).
        this._sessionIdentitySyncService.reloadOnIdentitySwitchUntil(this._destroyRef);

        this._i18nService.setScope('loggedin');

        // Extract userId into a computed to avoid re-running the effect when the user object
        // changes reference but the id stays the same (e.g. updateUserLastVisitedRestaurantId).
        const userId = computed(() => this.sessionStore.user()?.id);
        effect((onCleanup) => {
            const currentUserId = userId();

            if (currentUserId) {
                untracked(() => this._realtimeMessagingService.initializeIfNeeded(currentUserId));
            }

            // onCleanup is called automatically when the layout is destroyed (logout)
            // or if the user id changes (should not happen)
            onCleanup(() => {
                untracked(() => this._realtimeMessagingService.disconnect());
            });
        });
    }
}
